<template>
  <div class="flex h-full flex-col justify-between">
    <div>
      <RevInfoBlock
        v-if="apiError"
        class="mt-16"
        :icon="IconWarning"
        :title="apiError.title"
        variant="danger"
      >
        {{ apiError.detail }}
      </RevInfoBlock>

      <div class="mb-16">
        <span class="body-1">{{ i18n(translations.subtitle) }}</span>
        <span class="body-2">{{ i18n(translations.infoText) }}</span>
      </div>

      <RevForm :id="FORM_ID" :initial-values :validate @submit="handleSubmit">
        <template #default="{ values, errors }">
          <p class="body-1-bold my-16">
            {{ i18n(translations.selectOrderLines) }}
          </p>

          <RevInputSelect
            id="order-lines"
            v-model="values.orderLineId"
            class="mb-16"
            :disabled="order.orderLines.length === 1"
            :error="errors.orderLineId"
            :label="i18n(translations.orderLines)"
            :options="orderLinesOptions"
          />

          <p class="body-1-bold my-16">
            {{ i18n(translations.prompt) }}
          </p>

          <RevInputTextarea
            id="message"
            v-model="values.message"
            data-qa="care-message"
            :error="errors.message"
            :label="i18n(translations.message)"
            :rows="5"
          />
        </template>
      </RevForm>
    </div>

    <div class="flex flex-col items-center">
      <RevButton
        class="mb-16"
        data-qa="confirm-cancel-orderline"
        :form="FORM_ID"
        full-width="always"
        :loading="isLoading"
        type="submit"
        variant="primary"
      >
        {{ i18n(translations.confirm) }}
      </RevButton>
    </div>
  </div>
</template>

<script setup lang="ts">
import { navigateTo } from '#imports'
import { computed, ref } from 'vue'

import { HttpApiError } from '@backmarket/http-api'
import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { toNuxtLink } from '@backmarket/nuxt-module-navigation/toNuxtLink'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { RevButton } from '@ds/components/Button'
import { RevForm, makeValidate, required } from '@ds/components/Form'
import { RevInfoBlock } from '@ds/components/InfoBlock'
import { RevInputSelect } from '@ds/components/InputSelect'
import { RevInputTextarea } from '@ds/components/InputTextarea'
import { IconWarning } from '@ds/icons/IconWarning'

import type { Order } from '~/scopes/orders/pages/components/Tab/Tab.types'

import { FORM_ID } from './ContactCustomer.constants'
import translations from './ContactCustomer.translations'

const $httpFetch = useHttpFetch()

type FormValues = {
  message: string
  orderLineId: string
}

const props = defineProps<{
  order: Order
  currentIssueType?: 'OTHER' | 'DELIVERY_ISSUE'
}>()

const i18n = useI18n()
const { openSuccessToast } = useTheToast()

const apiError = ref<null | HttpApiError>(null)
const isLoading = ref(false)
const orderLinesOptions = computed(() =>
  props.order.orderLines.map((orderLine) => ({
    label: orderLine.id.toString(),
    value: String(orderLine.id),
  })),
)
const initialValues = computed(() => ({
  message: '',
  orderLineId:
    props.order.orderLines.length === 1
      ? props.order.orderLines[0].id.toString()
      : '',
}))
const validate = makeValidate<FormValues>({
  message: required(i18n(translations.required)),
  orderLineId: required(i18n(translations.required)),
})

async function handleSubmit({ message, orderLineId }: FormValues) {
  try {
    isLoading.value = true
    apiError.value = null

    const { redirectUrl } = await $httpFetch(
      '/bm/customer_request/orderline/:orderLineId/seller_requests',
      {
        method: 'POST',
        pathParams: {
          orderLineId,
        },
        body: {
          message,
          sellerIssue: props.currentIssueType,
        },
      },
    )

    if (!redirectUrl) {
      throw new Error('No redirect URL provided in the response')
    }

    openSuccessToast({
      content: i18n(translations.successMessage),
    })

    await navigateTo(toNuxtLink(redirectUrl))
  } catch (error) {
    apiError.value = HttpApiError.fromAnyError(error as Error)
  } finally {
    isLoading.value = false
  }
}
</script>
